import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { DatePicker, InfoCard, SolidCard } from "@utilifeed/components";
import { useDataApi } from "@core/apis";
import { getEnergyKpisBySubstationOptions } from "@core/apis/shared";

import QuickOptionButtonGroup from "../shared/QuickOptionButtonGroup";

type EnergyKpisProps = {
  substationId: string;
  networkUid?: string;
};

// First of July of the current year, or last year if it's before July
const getDefaultJulyFirst = () => {
  const now = DateTime.now();
  return now.month >= 7
    ? now.set({ month: 7, day: 1 }).startOf("day")
    : now.minus({ years: 1 }).set({ month: 7, day: 1 }).startOf("day");
};

const QUICK_DATE_OPTIONS = [
  {
    label: "text_ytd",
    value: "ytd",
    getDate: () => DateTime.now().startOf("year"),
  },
  {
    label: "text_last_3_months",
    value: "last3Months",
    getDate: () => DateTime.now().minus({ months: 3 }).startOf("day"),
  },
  {
    label: "text_start_of_month",
    value: "startOfMonth",
    getDate: () => DateTime.now().startOf("month"),
  },
];

export const EnergyKpis = ({ substationId, networkUid }: EnergyKpisProps) => {
  const dataApi = useDataApi();
  const { t } = useTranslation(["propai_properties", "propai", "_common"]);

  const [fromDate, setFromDate] = useState<DateTime>(getDefaultJulyFirst());
  const [selectedQuickOption, setSelectedQuickOption] = useState<string>("");

  const {
    data: energyKpisData,
    isLoading,
    error,
  } = useQuery(getEnergyKpisBySubstationOptions(dataApi, substationId, fromDate, networkUid));

  const optivarmeData = useMemo(() => {
    if (!energyKpisData) return { energyConsumption: 0, energyCost: 0 };
    const totalForAllFlexstations = energyKpisData.collection.reduce(
      (acc, curr) => {
        acc.total_energy_use += curr.total_energy_use;
        acc.total_energy_cost += curr.total_energy_cost;
        return acc;
      },
      { total_energy_use: 0, total_energy_cost: 0 }
    );

    return {
      energyConsumption: totalForAllFlexstations.total_energy_use,
      energyCost: totalForAllFlexstations.total_energy_cost,
    };
  }, [energyKpisData]);

  const handleQuickOptionChange = (option: { label: string; value: string }) => {
    const quickOption = QUICK_DATE_OPTIONS.find((opt) => opt.value === option.value);
    if (quickOption) {
      setFromDate(quickOption.getDate());
      setSelectedQuickOption(option.value);
    }
  };

  const handleDatePickerChange = (newDate: DateTime | null | undefined) => {
    if (newDate) {
      setFromDate(newDate.startOf("day"));
      setSelectedQuickOption("");
    }
  };

  return (
    <SolidCard
      title={"Optivarme"}
      titleAddon={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="labelLarge" color="text.secondary">
            {t("text_from", { ns: "_common" })}
          </Typography>
          <DatePicker
            value={fromDate}
            onChange={handleDatePickerChange}
            minDate={DateTime.fromObject({ year: 2000, month: 1, day: 1 })}
            maxDate={DateTime.now()}
            views={["year", "month", "day"]}
            format="yyyy-MM-dd"
            inputProps={{
              sx: {
                minWidth: 120,
                "& .MuiInputBase-root": {
                  paddingRight: 0,
                },
              },
            }}
          />
          <QuickOptionButtonGroup
            options={QUICK_DATE_OPTIONS}
            activeValue={selectedQuickOption}
            onChange={handleQuickOptionChange}
            ariaLabel="Date range quick options"
            isFullWidth={false}
          />
        </Box>
      }
    >
      <Box display="flex" flexDirection="row" gap={2} width="100%" justifyContent="space-between">
        <InfoCard
          title={t("impact_on_energy_use")}
          value={optivarmeData.energyConsumption}
          unit={"kWh"}
          color="tertiary"
          loading={isLoading}
          error={!!error}
          errorMessage={error?.message}
          isCardRounded
        />
        <InfoCard
          title={t("impact_on_energy_cost")}
          value={optivarmeData.energyCost}
          unit={"NOK"}
          color="tertiary"
          loading={isLoading}
          error={!!error}
          errorMessage={error?.message}
          isCardRounded
        />
      </Box>
    </SolidCard>
  );
};
